import type { IMetaData, IRecord } from "../types/scad";

export interface RecordsComposable {
  meta: MetaComposable;
  data: Ref<{
    records: Indexable[];
    total: number;
    recordMap: { [key: string]: Indexable };
  } | null>;
  loading: ComputedRef<boolean>;
  refresh: (opts?: any | undefined) => Promise<any>;
  fetch: (opts?: any | undefined) => Promise<any>;
  save(savableRecord: { [key: string]: any }): Promise<any>;
  deleteRecord(pkValue: number | string): Promise<any>;
  saveOptions: Ref<{
    url: string | null;
    injectedData: Indexable;
    method: "POST" | "PUT" | "PATCH";
  }>;
  filters: Ref<Indexable>;
  query: Ref<Indexable>;
  sort: Ref<{
    field: string | null;
    direction: number;
  }>;
  skip: Ref<number>;
  take: Ref<number>;
}

export default function useRecords(
  table: string,
  schema: string = "public",
  meta: MetaComposable = useMeta(table, schema),
  watchFilters: boolean = true,
): RecordsComposable {
  if (!meta?.data?.value) {
    throw new Error("Meta configuration undefined");
  }

  const pkCol = () => meta.data.value?.primaryKeyColumnNamePc ?? "Id";

  // PARAMETER HANDLING
  const explicitLoading = ref<boolean>(false);
  const skip = ref<number>(0);
  const take = ref<number>(meta.data.value?.data.Table.RecordsPerPage ?? 1);
  interface ISort {
    field: string | null;
    direction: number;
  }
  const sort = ref<ISort>({
    field: meta.data.value.data.Table.OrderBy?.[0]?.startsWith("-")
      ? meta.data.value.data.Table.OrderBy[0].substring(1)
      : meta.data.value.data.Table.OrderBy?.[0] || null,
    direction: meta.data.value.data.Table.OrderBy?.[0]?.startsWith("-")
      ? -1
      : 0,
  });

  // const sort = ref<ISort>({
  //   field: null,
  //   direction: 0,
  // });

  const filters = ref<Indexable>({});
  const query = ref<Indexable>({});

  const watchedProperties: Ref[] = [skip, take, sort];

  if (watchFilters) {
    watchedProperties.push(filters);
  }

  const {
    data: records,
    pending,
    refresh,
    execute,
    status,
  } = useFetch(
    meta.data.value?.data.Table.ReadRecordsRoute
      ? `/scad/${meta.data.value?.data.Table.ReadRecordsRoute}`
      : `/scad/scadfw/records/${schema}/${table}`,
    {
      immediate: false,
      key: `records-${table}-${schema}`,
      query: {
        skip,
        take,
      },
      watch: watchedProperties,
      onRequest({ request, options }) {
        const sortFilter: any = {};

        if (sort.value.direction !== 0) {
          sortFilter["OrderBy"] =
            sort.value.direction < 0
              ? `-${sort.value.field}`
              : sort.value.field;
        }

        // alter filters to send arrays as strings (if any present)
        for (const key in filters.value) {
          if (Array.isArray(filters.value[key])) {
            filters.value[key] = `${filters.value[key].join(",")}`;
          }
        }

        switch (meta.data.value?.data.Table.FilterQueryType?.toUpperCase()) {
          case "SERIALIZE":
          case "SERIALIZED":
            // Handle serialization
            options.query = {
              ...options.query,
              Filters: JSON.stringify({
                ...(filters.value || {}),
                ...sortFilter,
                ...query.value,
              }),
            };
            break;
          case "BOTH":
            options.query = {
              ...options.query,
              ...filters.value,
              ...sortFilter,
              ...query.value,
              Filters: JSON.stringify({
                ...(filters.value || {}),
                ...sortFilter,
                ...query.value,
              }),
            };
            break;
          default:
            options.query = {
              ...options.query,
              ...filters.value,
              ...sortFilter,
              ...query.value,
            };
        }
      },
      onRequestError({ request, options, error }) {
        // Handle the request errors
        console.error("Error fetching records:", error);
      },
      onResponseError({ request, response, options }) {
        // Handle the response errors
        console.error("Error in response while fetching records:", response);
      },
      transform: (
        response: IRecord,
      ): {
        records: Indexable[];
        total: number;
        recordMap: { [key: string]: Indexable };
      } => {
        const recordMap: Indexable = {};
        response.Results.forEach((record) => {
          meta.data.value?.columns.forEach((column) => {
            if (column.ControlType === "multiselect") {
              if (typeof record[column.ColumnName] === "string") {
                record[column.ColumnName] = record[column.ColumnName]
                  .replace("[", "")
                  .replace("]", "")
                  .split(",")
                  .map((value: string) => {
                    const trimmedValue = value.trim();

                    return isNaN(trimmedValue as any)
                      ? trimmedValue
                      : parseInt(trimmedValue);
                  });
              }
            }

            if (column.ControlType === "treeselect") {
              if (typeof record[column.ColumnName] === "string") {
                //map the string into an array
                record[column.ColumnName] = record[column.ColumnName]
                  //.slice(1, -1)
                  .split(",")
                  .map((value: string) => {
                    const trimmedValue = value.trim();

                    return isNaN(trimmedValue as any)
                      ? trimmedValue
                      : parseInt(trimmedValue);
                  });
              } else if (typeof record[column.ColumnName] === "number") {
                let colValue: number = record[column.ColumnName];
                record[column.ColumnName] = [colValue];
              }
            }
          });

          const prettyRecord: { [key: string]: any } = {};
          meta.data.value?.enabledGridColumns.forEach((column) => {
            prettyRecord[column.ColumnNamePc] = getPrettyRecordField(
              record,
              column,
              meta.data.value?.data as IMetaData,
            );
          });
          record._pretty = prettyRecord;
          recordMap[record[meta.data.value?.primaryKeyColumnNamePc ?? "Id"]] = record;
        });
        return {
          records: response.Results,
          recordMap,
          total: response.Total,
        };
      },
    },
  );

  const loading = computed<boolean>(() => {
    return pending.value || status.value != "success" || explicitLoading.value;
  });

  function applyAliases(data: Indexable) {
    const dataCopy = { ...data };
    const aliasedColumns = meta.data.value?.columns.filter((col) => {
      return (col.ColumnAlias ? true : false) && col.ColumnNamePc in data;
    });

    aliasedColumns?.forEach((col) => {
      if (!col.ColumnAlias) return;
      // Keyed by ColumnNamePc, matching the guard above and useRecord's copy of this function.
      // Reading data[col.ColumnName] took the snake_case name, which is never a key in the payload,
      // so the alias was written as undefined while the un-aliased key survived - a write through
      // this path would blank the base column rather than populate it (PFT-395).
      dataCopy[col.ColumnAlias] = data[col.ColumnNamePc];
      delete dataCopy[col.ColumnNamePc];
    });

    return dataCopy;
  }

  function cleanseData(data: Indexable) {
    const cleansedData: Indexable = {};
    // Remove all values that start with an underscore
    Object.keys(data).forEach((key) => {
      if (key === "Parent") return;
      if (key.startsWith("_")) return;
      if (key.endsWith("_Value")) return;
      if (typeof data[key] === "string") {
        data[key] = data[key].trim();
      }
      cleansedData[key] = data[key];
    });
    return cleansedData;
  }

  function processData(data: Indexable) {
    return applyAliases(cleanseData(data));
  }

  const saveOptions = ref<{
    url: string | null;
    injectedData: Indexable;
    method: "POST" | "PUT" | "PATCH";
  }>({
    url: null,
    injectedData: {},
    method: "POST",
  });

  async function saveRecord(savableRecord: {
    [key: string]: any;
  }): Promise<any> {
    // If url is set explicitly, use above all else
    if (saveOptions.value.url) {
    }
    // If record passed has a primary key, save record with PUT request
    else if (pkCol() in savableRecord && savableRecord[pkCol()]) {
      try {
        explicitLoading.value = true;
        const res = await $fetch(
          `/scad/${pluralize(
            meta.data?.value?.data.Table.UpdateRecordEndpoint
              ? meta.data?.value?.data.Table.UpdateRecordEndpoint
              : table,
          )}/${savableRecord[pkCol()]}`,
          {
            method: "PUT",
            body: {
              ...processData(savableRecord),
              ...saveOptions.value.injectedData,
            },
            onResponse() {
              refresh();
            },
          },
        );
        return res;
      } catch (e: any) {
        throw e;
      } finally {
        explicitLoading.value = false;
      }
    }
    // If other options not triggered, create a record and trigger a refresh
    else {
      try {
        explicitLoading.value = true;
        const res = await $fetch(
          `/scad/${pluralize(
            meta.data?.value?.data.Table.UpdateRecordEndpoint
              ? meta.data?.value?.data.Table.UpdateRecordEndpoint
              : table,
          )}`,
          {
            method: "POST",
            body: {
              ...processData(savableRecord),
              ...saveOptions.value.injectedData,
            },
            onResponse() {
              sort.value = { field: pkCol(), direction: -1 };
            },
          },
        );
        return res;
      } catch (e: any) {
        throw e;
      } finally {
        explicitLoading.value = false;
      }
    }
  }

  async function deleteRecord(pkValue: number | string): Promise<any> {
    explicitLoading.value = true;
    try {
      const result = await $fetch(
        `/scad/${pluralize(
          meta.data?.value?.data.Table.UpdateRecordEndpoint
            ? meta.data?.value?.data.Table.UpdateRecordEndpoint
            : table,
        )}/${pkValue}`,
        {
          method: "DELETE",
          onResponse() {
            refresh();
          },
          onResponseError({ response }) {
            throw new Error(
              response._data?.ResponseStatus?.Message ||
                response.statusText ||
                "Delete failed",
            );
          },
        },
      );
      return result;
    } finally {
      explicitLoading.value = false;
    }
  }

  return {
    save: saveRecord,
    deleteRecord,
    meta,
    data: records,
    loading,
    refresh,
    fetch: execute,
    filters,
    skip,
    take,
    sort,
    saveOptions,
    query,
  };
}
